import { useCallback, useEffect, useMemo } from "react";
import { NutrientWithTotal, useNutrition } from "./useNutrition";
import { useCache } from "./useCache";
import {
  useFilterAuthor,
  useFilterIsShaken,
  useFilterSpices,
  useFilterTitle,
  useSetFilter,
} from "./useFilter";
import labelSVG from "../assets/label.svg?raw"; // ?raw tells the bundler to treat it as text
import { MAX_QUANTITY_GRAM, Units } from "../data/schema";
import { useControls, folder } from "leva";
import { useCustomLoader } from "@/features/loading/hooks/useCustomLoader";
import type { ResourceKey } from "@/data/resources";
import { Texture, TextureLoader } from "three";

export function useLabel() {
  const { data } = useCache();
  const [spices] = useFilterSpices();
  const [isShaken] = useFilterIsShaken();
  const [title] = useFilterTitle();
  const [author] = useFilterAuthor();
  const { setFilter } = useSetFilter();
  const nutrition = useNutrition({ spices, cache: data });

  const [controls, setControls] = useControls(
    "Label",
    () => ({
      debug: folder(
        {
          show: false,
          centerX: { value: 325, min: 0, max: 500, step: 0.1 },
          title: {
            value: "",
            label: "Title",
            onEditStart: (v: string) =>
              setFilter((prev) => ({ ...prev, title: v })),
          },
          author: {
            value: "",
            label: "Author",
            onEditStart: (v: string) =>
              setFilter((prev) => ({ ...prev, author: v })),
          },
        },
        { collapsed: true, order: 2 }
      ),
    }),
    { collapsed: true }
  );

  const imageData = useMemo(() => {
    if (!labelSVG || !controls.show) return;

    //console.time("label");
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(labelSVG, "image/svg+xml");

    if (spices && spices.size && data) {
      // serving-size  // average of all spices tsp measure
      const servingSizeText = svgDoc.querySelector("#PLH-serving-size tspan");
      const servingSize = [...spices].reduce((sum, [id]) => { return sum + (data[id].portions?.[Units.TSP] ?? 0) }, 0) / spices.size; //prettier-ignore
      if (servingSizeText) {
        servingSizeText.textContent = `(${servingSize.toFixed(2)}g)`;
      }

      // total spices divided with x
      const servingsPerContainerText = svgDoc.querySelector("#PLH-servings tspan"); //prettier-ignore
      const servingsPerContainer = Math.round(MAX_QUANTITY_GRAM / servingSize);
      if (servingsPerContainerText) {
        servingsPerContainerText.textContent = servingsPerContainer.toString(); //prettier-ignore
      }

      // nutrition
      if (nutrition)
        nutrition.map((n: NutrientWithTotal) => {
          const value = svgDoc.getElementById(`${n.id}`);
          const percent = svgDoc.getElementById(`${n.id}%`);

          if (value && percent) {
            const valueTspan = value.querySelector("tspan");
            const percentTspan = percent.querySelector("tspan");

            if (valueTspan) {
              valueTspan.textContent = `${((n.total/servingsPerContainer).toFixed(0))}${n.unit !== "kcal" ? n.unit : ""}`; //prettier-ignore
            }
            if (percentTspan) {
              percentTspan.textContent = `${(n.total/servingsPerContainer).toFixed(0)}%` //prettier-ignore
            }
          }
        });
    }

    // meta data
    if (title && author) {
      const titleEl = svgDoc.querySelector("#PLH-title tspan");
      const authorEl = svgDoc.querySelector("#PLH-author tspan");

      if (titleEl && authorEl) {
        titleEl.textContent = title;
        authorEl.textContent = author;

        titleEl.setAttribute("x", (controls.centerX).toString()); //prettier-ignore
        authorEl.setAttribute("x", (controls.centerX).toString()); //prettier-ignore

        titleEl.setAttribute("text-anchor", "middle");
        authorEl.setAttribute("text-anchor", "middle");
      }
    }

    const serializer = new XMLSerializer();
    const imageData = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(serializer.serializeToString(svgDoc))))}`; // prettier-ignore

    //console.timeEnd("label");

    return imageData;
  }, [spices, title, author, data, nutrition, controls]);


  const tex = useCustomTexture("label", imageData)

  useEffect(() => {
    setControls({ show: isShaken });
  }, [setControls, isShaken]);

  return { imageData, texture: tex };
}

//** Custom loader to account for dynamic changes of the url */
const texLoader = new TextureLoader();
function useCustomTexture(key: ResourceKey, input?: string) {

  const onLoad = useCallback((tex: Texture) => {
    tex.flipY = false;
    tex.needsUpdate = true;
  }, []);

  const tex = useCustomLoader({
    key,
    input: input ?? null,
    loader: texLoader,
    onLoad,
  });

  return tex;
}
